import { axialToWorld, getMapAxialBounds, hexKey } from './hexGridMath';

export const MAX_ANALYSIS_DIMENSION = 2048;
export const MAX_ANALYZED_HEXES = 100000;

const loadMapImage = (imageUrl) => new Promise((resolve, reject) => {
  const image = new Image();
  image.crossOrigin = 'anonymous';
  image.onload = () => resolve(image);
  image.onerror = () => reject(new Error('image_load_failed'));
  image.src = imageUrl;
});

export const enumerateMapHexCenters = (grid, width, height) => {
  const bounds = getMapAxialBounds(grid, width, height);
  const keys = [];
  const points = [];
  for (let q = bounds.qMin; q <= bounds.qMax; q += 1) {
    for (let r = bounds.rMin; r <= bounds.rMax; r += 1) {
      const point = axialToWorld(q, r, grid);
      if (point.x < 0 || point.y < 0 || point.x > width || point.y > height) continue;
      keys.push(hexKey(q, r));
      points.push(point.x, point.y);
      if (keys.length > MAX_ANALYZED_HEXES) throw new Error('too_many_hexes');
    }
  }
  return { keys, centers: new Float32Array(points) };
};

export const analyzeTerrainColors = async ({ imageUrl, calibration, onProgress }) => {
  onProgress?.(0.02);
  const image = await loadMapImage(imageUrl);
  const sourceWidth = calibration.imageWidth || image.naturalWidth;
  const sourceHeight = calibration.imageHeight || image.naturalHeight;
  const analysisScale = Math.min(1, MAX_ANALYSIS_DIMENSION / Math.max(sourceWidth, sourceHeight));
  const width = Math.max(1, Math.round(sourceWidth * analysisScale));
  const height = Math.max(1, Math.round(sourceHeight * analysisScale));
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d', { willReadFrequently: true });
  context.drawImage(image, 0, 0, width, height);

  let imageData;
  try {
    imageData = context.getImageData(0, 0, width, height);
  } catch (error) {
    throw new Error('image_cors_blocked');
  }
  onProgress?.(0.1);

  const { keys, centers } = enumerateMapHexCenters(
    calibration.grid,
    sourceWidth,
    sourceHeight,
  );
  for (let index = 0; index < centers.length; index += 1) centers[index] *= analysisScale;

  return new Promise((resolve, reject) => {
    const worker = new Worker(new URL('./terrainAnalysis.worker.js', import.meta.url));
    worker.onmessage = ({ data }) => {
      if (data.type === 'progress') {
        onProgress?.(0.1 + data.value * 0.9);
        return;
      }
      if (data.type === 'complete') {
        const colors = new Uint8Array(data.colorsBuffer);
        const features = new Float32Array(data.featuresBuffer);
        const result = {};
        keys.forEach((key, index) => {
          result[key] = {
            r: colors[index * 3],
            g: colors[index * 3 + 1],
            b: colors[index * 3 + 2],
            features: {
              contrast: Number(features[index * 3].toFixed(4)),
              edgeDensity: Number(features[index * 3 + 1].toFixed(4)),
              darkCoverage: Number(features[index * 3 + 2].toFixed(4)),
            },
          };
        });
        worker.terminate();
        onProgress?.(1);
        resolve(result);
      }
    };
    worker.onerror = (event) => {
      worker.terminate();
      reject(new Error(event.message || 'analysis_worker_failed'));
    };
    worker.postMessage({
      pixelsBuffer: imageData.data.buffer,
      width,
      height,
      centersBuffer: centers.buffer,
      radius: calibration.grid.sizePx * analysisScale,
    }, [imageData.data.buffer, centers.buffer]);
  });
};
